/**
 * ItemSwitcher Component
 *
 * A reusable popover component for switching between items (workspaces, agents, etc.).
 * Displays a search bar, the current item with a check icon, a list of other items
 * to switch to, and an optional "Create New" button.
 *
 * Ported from legacy ItemSwitcher.tsx (frontend-tenant) -- adapted for verisa_v1.
 *
 * @module components/shared/ItemSwitcher
 */

import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { colors } from '../../theme/colors';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SwitcherItem {
  id: string;
  name: string;
}

export interface ItemSwitcherProps {
  /** Anchor element for the popover positioning */
  anchorEl: HTMLElement | null;
  /** Whether the popover is open */
  open: boolean;
  /** Callback when the popover is closed */
  onClose: () => void;
  /** List of all items to display */
  items: SwitcherItem[];
  /** ID of the currently selected item */
  currentItemId?: string;
  /** Display name of the currently selected item */
  currentItemName?: string;
  /** Callback when an item is selected */
  onSelect: (item: SwitcherItem) => void;
  /** Optional callback for the "Create New" button; omit to hide the button */
  onCreateNew?: () => void;
  /** Placeholder text for the search input */
  searchPlaceholder?: string;
  /** Label for the "Current" section */
  currentLabel?: string;
  /** Label for the "Switch to" section */
  switchLabel?: string;
  /** Label for the "Create New" button */
  createLabel?: string;
  /** Optional unique key to force re-render when items change */
  uniqueKey?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ItemSwitcher: React.FC<ItemSwitcherProps> = ({
  anchorEl,
  open,
  onClose,
  items,
  currentItemId,
  currentItemName,
  onSelect,
  onCreateNew,
  searchPlaceholder,
  currentLabel,
  switchLabel,
  createLabel,
  uniqueKey = '',
}) => {
  const { _: lingui } = useLingui();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const otherItems = filteredItems.filter((item) => item.id !== currentItemId);

  return (
    <Popover
      key={`switcher-popover-${items.length}-${uniqueKey}`}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            width: 320,
            maxHeight: 400,
            mt: 1,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid',
            borderColor: 'divider',
          },
        },
      }}
    >
      <Box
        key={`switcher-list-${items.length}-${items.map((i) => i.id).join('-')}-${uniqueKey}`}
        sx={{ p: 2 }}
      >
        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder={searchPlaceholder || lingui(msg`Search...`)}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: colors.text.disabled }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              fontSize: '14px',
            },
          }}
        />

        {/* Current Item Label */}
        <Typography
          variant="caption"
          sx={{
            color: colors.text.secondary,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            mb: 1,
            display: 'block',
          }}
        >
          {currentLabel || <Trans>Current</Trans>}
        </Typography>

        {/* Current Item */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1,
            borderRadius: 1.5,
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: colors.text.primary,
            }}
          >
            {currentItemName || <Trans>No Item</Trans>}
          </Typography>
          <CheckIcon sx={{ fontSize: 18, color: colors.text.secondary, ml: 1 }} />
        </Box>

        {/* Other Items */}
        {otherItems.length > 0 && (
          <>
            <Typography
              variant="caption"
              sx={{
                color: colors.text.secondary,
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                mb: 1,
                display: 'block',
              }}
            >
              {switchLabel || <Trans>Switch to</Trans>}
            </Typography>

            {otherItems.map((item) => (
              <Box
                key={item.id}
                onClick={() => onSelect(item)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: colors.background.hover,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: colors.text.default,
                  }}
                >
                  {item.name}
                </Typography>
              </Box>
            ))}
          </>
        )}

        {/* Create New Button */}
        {onCreateNew && (
          <>
            <Divider sx={{ my: 2 }} />
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                onClose();
                onCreateNew();
              }}
              sx={{
                height: '40px',
                px: 2,
                py: 1,
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                textTransform: 'none',
                bgcolor: 'transparent',
                borderColor: 'divider',
                color: colors.text.primary,
                boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
                transition: 'all 0.2s ease',
                gap: 1,
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: colors.text.secondary,
                },
                '&:focus-visible': {
                  outline: '3px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '2px',
                  opacity: 0.5,
                },
                '& .MuiButton-startIcon': {
                  marginRight: 0.5,
                  '& svg': {
                    fontSize: '16px',
                  },
                },
              }}
            >
              {createLabel || <Trans>Create New</Trans>}
            </Button>
          </>
        )}
      </Box>
    </Popover>
  );
};

export default ItemSwitcher;
